<template>
  <div class="push-record-page">
    <div class="page-header">
      <div class="header-bg">
        <svg
          viewBox="0 0 800 200"
          preserveAspectRatio="none"
          class="header-svg"
        >
          <circle cx="100" cy="100" r="180" fill="rgba(255,255,255,0.04)" />
          <circle cx="650" cy="50" r="140" fill="rgba(6,182,212,0.18)" />
          <circle cx="780" cy="180" r="80" fill="rgba(255,255,255,0.06)" />
          <path
            d="M 0 150 Q 200 100 400 130 T 800 110"
            stroke="rgba(255,255,255,0.08)"
            stroke-width="1"
            fill="none"
          />
        </svg>
      </div>
      <div class="title-block">
        <h1 class="page-title">{{ t("UPGLV.pushRecordTitle") }}</h1>
        <p class="page-desc">{{ t("UPGLV.pushRecordDesc") }}</p>
      </div>
      <div class="header-actions">
        <span v-if="lastRefreshTime" class="last-refresh">
          <span class="live-dot" :class="{ active: autoRefresh }"></span>
          {{ t("UPGLV.lastRefresh") }}:{{ lastRefreshTime }}
        </span>
        <el-switch
          v-model="autoRefresh"
          :active-text="t('UPGLV.autoRefreshOn')"
          :inactive-text="t('UPGLV.autoRefreshOff')"
          class="auto-refresh-switch"
        />
        <button
          class="refresh-btn"
          :class="{ loading }"
          :disabled="loading"
          @click="handleRefresh"
        >
          <span class="ripple"></span>
          <el-icon class="refresh-icon"><Refresh /></el-icon>
          <span class="refresh-text">{{ t("UPGLV.refresh") }}</span>
        </button>
      </div>
    </div>

    <div class="stat-row">
      <div class="stat-card stat-total">
        <div class="stat-icon-wrap">
          <el-icon class="stat-icon"><Promotion /></el-icon>
        </div>
        <div class="stat-body">
          <div class="stat-label">{{ t("UPGLV.statTotal") }}</div>
          <div class="stat-value">{{ displayStats.total }}</div>
          <div class="stat-progress">
            <div
              class="stat-progress-bar"
              :style="{ width: displayStats.successRate + '%' }"
            ></div>
          </div>
          <div class="stat-tip">
            {{ t("UPGLV.statSuccessRate") }}
            <strong class="stat-rate">{{ displayStats.successRate }}%</strong>
            - {{ t("UPGLV.statUnitPage") }}
          </div>
        </div>
      </div>
      <div class="stat-card stat-completed">
        <div class="stat-icon-wrap">
          <el-icon class="stat-icon"><CircleCheck /></el-icon>
        </div>
        <div class="stat-body">
          <div class="stat-label">{{ t("UPGLV.statCompleted") }}</div>
          <div class="stat-value">{{ displayStats.completed }}</div>
          <div class="stat-tip">
            {{
              t("UPGLV.statSubCompleted", {
                0: displayStats.successCount,
                1: displayStats.partialCount,
              })
            }}
          </div>
        </div>
      </div>
      <div class="stat-card stat-processing">
        <div class="stat-icon-wrap">
          <el-icon class="stat-icon is-loading"><Loading /></el-icon>
        </div>
        <div class="stat-body">
          <div class="stat-label">{{ t("UPGLV.statProcessing") }}</div>
          <div class="stat-value">{{ displayStats.processing }}</div>
          <div class="stat-tip">
            {{
              t("UPGLV.statSubProcessing", {
                0: displayStats.pending,
                1: displayStats.upgrading,
              })
            }}
          </div>
        </div>
      </div>
      <div class="stat-card stat-failed">
        <div class="stat-icon-wrap">
          <el-icon class="stat-icon"><CircleClose /></el-icon>
        </div>
        <div class="stat-body">
          <div class="stat-label">{{ t("UPGLV.statFailed") }}</div>
          <div class="stat-value">{{ displayStats.failed }}</div>
          <div class="stat-tip">
            {{
              t("UPGLV.statSubFailed", {
                0: displayStats.rejected,
                1: displayStats.upgradeFailed,
              })
            }}
          </div>
        </div>
      </div>
      <div class="stat-card stat-expired">
        <div class="stat-icon-wrap">
          <el-icon class="stat-icon"><Clock /></el-icon>
        </div>
        <div class="stat-body">
          <div class="stat-label">{{ t("UPGLV.statExpired") }}</div>
          <div class="stat-value">{{ displayStats.expired }}</div>
          <div class="stat-tip">{{ t("UPGLV.statExpiredTip") }}</div>
        </div>
      </div>
    </div>

    <div class="filter-bar">
      <el-select
        v-model="query.type"
        :placeholder="t('UPGLV.filterTypeAll')"
        class="filter-select filter-type"
        @change="handleQuery"
      >
        <el-option
          v-for="opt in typeOptions"
          :key="opt.value ?? 'all'"
          :label="opt.label"
          :value="opt.value ?? ''"
        />
      </el-select>
      <el-select
        v-model="query.resultStatus"
        :placeholder="t('UPGLV.filterResultStatusAll')"
        clearable
        class="filter-select"
        @change="handleQuery"
      >
        <el-option
          v-for="opt in resultStatusOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        />
      </el-select>
      <el-select
        v-model="query.customerAttitude"
        :placeholder="t('UPGLV.filterAttitudeAll')"
        clearable
        class="filter-select"
        @change="handleQuery"
      >
        <el-option
          v-for="opt in attitudeOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        />
      </el-select>
      <el-date-picker
        v-model="query.timeRange"
        type="datetimerange"
        range-separator="-"
        :start-placeholder="t('UPGLV.timeRangePlaceholder')"
        :end-placeholder="t('UPGLV.timeRangePlaceholder')"
        class="filter-date"
        @change="handleQuery"
      />
      <button class="action-btn btn-primary" @click="handleQuery">
        <el-icon class="btn-icon"><Search /></el-icon>
        <span>{{ t("UPGLV.search") }}</span>
      </button>
      <button class="action-btn btn-ghost" @click="handleReset">
        <el-icon class="btn-icon btn-icon-spin"><RefreshLeft /></el-icon>
        <span>{{ t("UPGLV.reset") }}</span>
      </button>
    </div>

    <div class="table-card">
      <el-table
        v-loading="loading"
        :data="recordList"
        row-key="id"
        class="record-table"
        :row-class-name="getRowClassName"
        @expand-change="handleExpandChange"
      >
        <template #empty>
          <div class="empty-state">
            <el-icon class="empty-icon"><DocumentRemove /></el-icon>
            <div class="empty-text">{{ t("UPGLV.noData") }}</div>
            <div class="empty-hint">{{ t("UPGLV.noDataHint") }}</div>
          </div>
        </template>

        <el-table-column type="expand" width="48">
          <template #default="scope">
            <div
              v-if="scope.row.type === 2 || scope.row.type === 4"
              class="expand-area"
            >
              <el-table
                v-if="expandedItems[scope.row.id]"
                :data="expandedItems[scope.row.id]"
                size="small"
                border
                class="item-table"
              >
                <el-table-column
                  :label="t('UPGLV.itemColSeq')"
                  prop="sequence"
                  width="60"
                  align="center"
                />
                <el-table-column
                  :label="t('UPGLV.itemColDevice')"
                  prop="devName"
                  min-width="120"
                >
                  <template #default="{ row }">{{
                    translateName(row.devName)
                  }}</template>
                </el-table-column>
                <el-table-column :label="t('UPGLV.colType')" width="100">
                  <template #default="s">
                    {{ getSubTypeName(s.row.subType) }}
                  </template>
                </el-table-column>
                <el-table-column
                  :label="t('UPGLV.itemColFile')"
                  prop="fileName"
                  min-width="160"
                  show-overflow-tooltip
                />
                <el-table-column
                  :label="t('UPGLV.itemColPush')"
                  width="90"
                  align="center"
                >
                  <template #default="s">
                    <el-tag :type="tagOfPush(s.row.pushStatus)" size="small">
                      {{ nameOfPush(s.row.pushStatus) }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column
                  :label="t('UPGLV.itemColResponse')"
                  width="90"
                  align="center"
                >
                  <template #default="s">
                    <el-tag
                      :type="tagOfResp(s.row.responseStatus)"
                      size="small"
                    >
                      {{ nameOfResp(s.row.responseStatus) }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column
                  :label="t('UPGLV.itemColResult')"
                  width="90"
                  align="center"
                >
                  <template #default="s">
                    <el-tag
                      :type="tagOfItemResult(s.row.resultStatus)"
                      size="small"
                    >
                      {{ nameOfItemResult(s.row.resultStatus) }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column
                  :label="t('UPGLV.itemColError')"
                  prop="errorCode"
                  width="80"
                  align="center"
                >
                  <template #default="s">{{ s.row.errorCode || "-" }}</template>
                </el-table-column>
              </el-table>
              <div v-else class="expand-loading">
                <el-icon class="is-loading"><Loading /></el-icon>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column :label="t('UPGLV.colType')" width="110">
          <template #default="scope">
            <div class="type-cell">
              <el-icon
                class="type-icon"
                :style="{ color: typeMeta(scope.row.type).color }"
              >
                <component :is="typeMeta(scope.row.type).icon" />
              </el-icon>
              <el-tag
                :type="typeMeta(scope.row.type).tag"
                size="small"
                effect="light"
              >
                {{ typeMeta(scope.row.type).label }}
              </el-tag>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          :label="t('UPGLV.colPlan')"
          width="200"
          show-overflow-tooltip
        >
          <template #default="scope">
            <div class="plan-cell">
              <span class="plan-name">{{ scope.row.planName || "-" }}</span>
              <span class="plan-ref">{{
                shortId(scope.row.refId || scope.row.id, 12)
              }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          :label="t('UPGLV.colShip')"
          width="150"
          show-overflow-tooltip
        >
          <template #default="scope">
            <div class="ship-cell">
              <span class="ship-name">{{ scope.row.shipName || "-" }}</span>
              <span class="ship-id">{{ shortId(scope.row.shipId, 8) }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          :label="t('UPGLV.colAttitude')"
          width="80"
          align="center"
        >
          <template #default="scope">
            <el-tag
              v-if="scope.row.needConsent === 1"
              :type="tagOfAttitude(scope.row.customerAttitude)"
              size="small"
              effect="plain"
            >
              {{ nameOfAttitude(scope.row.customerAttitude) }}
            </el-tag>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>

        <el-table-column
          :label="t('UPGLV.colResult')"
          width="100"
          align="center"
        >
          <template #default="scope">
            <el-tag
              :type="tagOfResult(scope.row.resultStatus)"
              size="small"
              effect="dark"
            >
              {{ nameOfResultByType(scope.row.resultStatus, scope.row.type) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column
          :label="t('UPGLV.colProgress')"
          width="150"
          align="center"
        >
          <template #default="scope">
            <div v-if="scope.row.totalItems > 0" class="progress-cell">
              <el-progress
                :percentage="progressPct(scope.row)"
                :status="progressStatus(scope.row.resultStatus)"
                :stroke-width="8"
              />
              <span class="progress-text">
                {{ scope.row.successItems }}/{{ scope.row.totalItems }}
              </span>
            </div>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>

        <el-table-column :label="t('UPGLV.colTime')" width="200">
          <template #default="scope">
            <div class="time-cell">
              <div class="time-row">
                <span class="time-tag">{{ t("UPGLV.timelinePush") }}</span>
                <span>{{ formatTime(scope.row.pushTime) }}</span>
              </div>
              <div class="time-row time-sub">
                <span class="time-tag">{{ t("UPGLV.timelineResult") }}</span>
                <span>{{ formatTime(scope.row.resultTime) }}</span>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          :label="t('UPGLV.colActions')"
          width="130"
          align="center"
          fixed="right"
        >
          <template #default="scope">
            <div class="row-actions">
              <el-tooltip :content="t('UPGLV.actionDetail')" placement="top">
                <button
                  class="row-action-btn btn-view"
                  @click.stop="openDetail(scope.row)"
                >
                  <el-icon><View /></el-icon>
                </button>
              </el-tooltip>
              <el-tooltip
                v-if="canRetry(scope.row)"
                :content="t('UPGLV.actionRetry')"
                placement="top"
              >
                <button
                  class="row-action-btn btn-retry"
                  :disabled="loadingMap.get('retry-' + scope.row.id)"
                  @click.stop="handleRetry(scope.row)"
                >
                  <el-icon v-if="!loadingMap.get('retry-' + scope.row.id)"
                    ><RefreshRight
                  /></el-icon>
                  <el-icon v-else class="is-loading"><Loading /></el-icon>
                </button>
              </el-tooltip>
              <el-tooltip
                v-if="canCancel(scope.row)"
                :content="t('UPGLV.actionCancel')"
                placement="top"
              >
                <button
                  class="row-action-btn btn-danger"
                  :disabled="loadingMap.get('cancel-' + scope.row.id)"
                  @click.stop="handleCancel(scope.row)"
                >
                  <el-icon v-if="!loadingMap.get('cancel-' + scope.row.id)"
                    ><CircleClose
                  /></el-icon>
                  <el-icon v-else class="is-loading"><Loading /></el-icon>
                </button>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrapper">
        <el-pagination
          v-model:current-page="currentPage"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next, jumper"
          background
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <el-drawer
      v-model="detailVisible"
      direction="rtl"
      size="560px"
      destroy-on-close
      @open="onDrawerOpen"
    >
      <template #header>
        <div class="drawer-header">
          <span class="drawer-title">{{ t("UPGLV.detailTitle") }}</span>
          <div v-if="currentDetail" class="drawer-actions">
            <el-tooltip
              v-if="canRetry(currentDetail)"
              :content="t('UPGLV.actionRetry')"
              placement="top"
            >
              <button
                class="drawer-action-btn btn-retry"
                :disabled="loadingMap.get('retry-' + currentDetail.id)"
                @click="handleRetry(currentDetail)"
              >
                <el-icon v-if="!loadingMap.get('retry-' + currentDetail.id)"
                  ><RefreshRight
                /></el-icon>
                <el-icon v-else class="is-loading"><Loading /></el-icon>
                <span>{{ t("UPGLV.actionRetry") }}</span>
              </button>
            </el-tooltip>
            <el-tooltip
              v-if="canCancel(currentDetail)"
              :content="t('UPGLV.actionCancel')"
              placement="top"
            >
              <button
                class="drawer-action-btn btn-danger"
                :disabled="loadingMap.get('cancel-' + currentDetail.id)"
                @click="handleCancel(currentDetail)"
              >
                <el-icon v-if="!loadingMap.get('cancel-' + currentDetail.id)"
                  ><CircleClose
                /></el-icon>
                <el-icon v-else class="is-loading"><Loading /></el-icon>
                <span>{{ t("UPGLV.actionCancel") }}</span>
              </button>
            </el-tooltip>
          </div>
        </div>
      </template>
      <div v-if="currentDetail" class="detail-content">
        <div class="detail-header">
          <el-tag
            :type="typeMeta(currentDetail.type).tag"
            effect="dark"
            size="large"
          >
            {{ typeMeta(currentDetail.type).label }}
          </el-tag>
          <el-tag
            :type="tagOfResult(currentDetail.resultStatus)"
            effect="dark"
            size="large"
          >
            {{ nameOfResult(currentDetail.resultStatus) }}
          </el-tag>
          <div class="detail-plan-name">
            {{ currentDetail.planName || "-" }}
          </div>
        </div>

        <div class="detail-section">
          <div class="section-title">{{ t("UPGLV.detailBasic") }}</div>
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item :label="t('UPGLV.labelShip')">
              {{ currentDetail.shipName || "-" }}
            </el-descriptions-item>
            <el-descriptions-item :label="t('UPGLV.colEndpoint')">
              <span class="mono">{{ currentDetail.endpointId || "-" }}</span>
            </el-descriptions-item>
            <el-descriptions-item :label="t('UPGLV.labelPlan')">
              <span class="mono">{{
                currentDetail.refId || currentDetail.id
              }}</span>
            </el-descriptions-item>
            <el-descriptions-item :label="t('UPGLV.labelNeedConsent')">
              {{
                currentDetail.needConsent === 1
                  ? t("UPGLV.labelYes")
                  : t("UPGLV.labelNo")
              }}
            </el-descriptions-item>
            <el-descriptions-item
              v-if="currentDetail.needConsent === 1"
              :label="t('UPGLV.colAttitude')"
            >
              <el-tag
                :type="tagOfAttitude(currentDetail.customerAttitude)"
                size="small"
              >
                {{ nameOfAttitude(currentDetail.customerAttitude) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item :label="t('UPGLV.labelOperator')">
              {{ currentDetail.managerId || "-" }}
            </el-descriptions-item>
            <el-descriptions-item :label="t('UPGLV.labelDetail')" :span="2">
              <template v-if="isUrlLike(currentDetail.detail)">
                <!-- 历史上 detail 字段被误用来存 URL（已迁移到 filePath 字段），此处不再展示 -->
                <span class="muted">{{ t("UPGLV.labelDetailNone") }}</span>
              </template>
              <template v-else>
                {{ currentDetail.detail || "-" }}
              </template>
            </el-descriptions-item>
          </el-descriptions>
        </div>

        <div v-if="fileInfo" class="detail-section">
          <div class="section-title">{{ t("UPGLV.detailFile") }}</div>
          <div class="file-card">
            <el-icon class="file-icon"><Document /></el-icon>
            <div class="file-info">
              <div class="file-name">{{ fileInfo.fileName }}</div>
              <div v-if="fileInfo.fileMd5" class="file-meta">
                <span class="meta-label">MD5</span>
                <span class="meta-value mono">{{ fileInfo.fileMd5 }}</span>
              </div>
              <!-- Svc001 三文件：MD5 校验文件 / 签名文件（extraJson） -->
              <div v-if="fileInfo.md5FileName" class="file-meta">
                <span class="meta-label">MD5文件</span>
                <span class="meta-value mono">{{ fileInfo.md5FileName }}</span>
              </div>
              <div v-if="fileInfo.signFileName" class="file-meta">
                <span class="meta-label">签名文件</span>
                <span class="meta-value mono">{{ fileInfo.signFileName }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <div class="section-title">{{ t("UPGLV.detailTimeline") }}</div>
          <el-timeline>
            <el-timeline-item
              :timestamp="formatTime(currentDetail.createtime)"
              placement="top"
              color="#909399"
            >
              <span class="timeline-label">{{
                t("UPGLV.timelineCreate")
              }}</span>
            </el-timeline-item>
            <el-timeline-item
              :timestamp="formatTime(currentDetail.pushTime)"
              placement="top"
              :color="currentDetail.pushTime ? '#409eff' : '#909399'"
            >
              <span class="timeline-label">{{ t("UPGLV.timelinePush") }}</span>
              <el-tag
                v-if="currentDetail.pushTime"
                :type="tagOfPush(currentDetail.pushStatus)"
                size="small"
              >
                {{ nameOfPush(currentDetail.pushStatus) }}
              </el-tag>
              <span v-else class="muted">{{ t("UPGLV.timelinePending") }}</span>
            </el-timeline-item>
            <el-timeline-item
              :timestamp="formatTime(currentDetail.responseTime)"
              placement="top"
              :color="timelineColorOfResp(currentDetail.responseStatus)"
            >
              <span class="timeline-label">{{
                t("UPGLV.timelineResponse")
              }}</span>
              <el-tag
                v-if="currentDetail.responseTime"
                :type="tagOfResp(currentDetail.responseStatus)"
                size="small"
              >
                {{ nameOfResp(currentDetail.responseStatus) }}
              </el-tag>
              <span v-else class="muted">{{ t("UPGLV.timelinePending") }}</span>
            </el-timeline-item>
            <el-timeline-item
              :timestamp="formatTime(currentDetail.resultTime)"
              placement="top"
              :color="timelineColorOfResult(currentDetail.resultStatus)"
            >
              <span class="timeline-label">{{
                t("UPGLV.timelineResult")
              }}</span>
              <el-tag
                v-if="currentDetail.resultTime"
                :type="tagOfResult(currentDetail.resultStatus)"
                size="small"
                effect="dark"
              >
                {{ nameOfResult(currentDetail.resultStatus) }}
              </el-tag>
              <span v-else class="muted">{{ t("UPGLV.timelinePending") }}</span>
            </el-timeline-item>
          </el-timeline>
        </div>

        <div
          v-if="currentDetail.type === 2 || currentDetail.type === 4"
          class="detail-section"
        >
          <div class="section-title">
            {{
              currentDetail.type === 4
                ? t("UPGLV.detailProtocolItems")
                : t("UPGLV.detailItems")
            }}
            <span v-if="detailItems.length" class="section-count"
              >({{ detailItems.length }})</span
            >
          </div>
          <div v-if="detailItemsLoading" class="expand-loading">
            <el-icon class="is-loading"><Loading /></el-icon>
          </div>
          <el-table
            v-else-if="detailItems.length"
            :data="detailItems"
            size="small"
            border
          >
            <el-table-column
              :label="t('UPGLV.itemColSeq')"
              prop="sequence"
              width="60"
              align="center"
            />
            <el-table-column
              :label="t('UPGLV.itemColDevice')"
              prop="devName"
              min-width="120"
              show-overflow-tooltip
            >
              <template #default="{ row }">{{
                translateName(row.devName)
              }}</template>
            </el-table-column>
            <el-table-column
              :label="t('UPGLV.itemColFile')"
              prop="fileName"
              min-width="140"
              show-overflow-tooltip
            />
            <el-table-column
              :label="t('UPGLV.itemColResult')"
              width="100"
              align="center"
            >
              <template #default="s">
                <el-tag
                  :type="tagOfItemResult(s.row.resultStatus)"
                  size="small"
                >
                  {{ nameOfItemResult(s.row.resultStatus) }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
          <div v-else class="muted">{{ t("UPGLV.detailItemsEmpty") }}</div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script lang="ts" setup>
import { loadTranslations, translateName } from "@/composables/useTranslation";
import {
  getPushRecordListApi,
  getPushRecordItemsApi,
  cancelPushRecordApi,
  type PushRecord,
  type PushRecordItem,
} from "@/api/operation/upgradeApi";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  Refresh,
  RefreshLeft,
  RefreshRight,
  Search,
  Loading,
  View,
  DocumentRemove,
  Cpu,
  Cellphone,
  Connection,
  Lightning,
  Promotion,
  CircleCheck,
  CircleClose,
  Clock,
  Document,
} from "@element-plus/icons-vue";
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const recordList = ref<PushRecord[]>([]);
const expandedItems = ref<Record<string, PushRecordItem[]>>({});
const loading = ref(false);
const loadingMap = ref<Map<string, boolean>>(new Map());
const currentPage = ref(1);
const pageSize = 20;
const total = ref(0);

const query = reactive<{
  type: number | null;
  resultStatus: number | null;
  customerAttitude: number | null;
  timeRange: [Date, Date] | [];
}>({
  type: null,
  resultStatus: null,
  customerAttitude: null,
  timeRange: [],
});

const autoRefresh = ref(false);
const lastRefreshTime = ref<string>("");
let refreshTimer: number | null = null;
const REFRESH_INTERVAL = 10000;

const detailVisible = ref(false);
const currentDetail = ref<PushRecord | null>(null);
const detailItems = ref<PushRecordItem[]>([]);
const detailItemsLoading = ref(false);

const typeMeta = (type: number) => {
  const map = {
    1: {
      label: t("UPGLV.filterTypeSvc001"),
      tag: "primary",
      color: "#409EFF",
      icon: Cpu,
    },
    2: {
      label: t("UPGLV.filterTypeSvc004"),
      tag: "success",
      color: "#67C23A",
      icon: Cellphone,
    },
    3: {
      label: t("UPGLV.filterTypeSvc003"),
      tag: "warning",
      color: "#E6A23C",
      icon: Connection,
    },
    4: {
      label: t("UPGLV.filterTypeSvc005"),
      tag: "info",
      color: "#909399",
      icon: Lightning,
    },
  } as const;
  return (
    map[type as 1 | 2 | 3 | 4] ?? {
      label: "未知",
      tag: "",
      color: "#909399",
      icon: DocumentRemove,
    }
  );
};

const typeOptions = computed(() => [
  { label: t("UPGLV.filterTypeAll"), value: null },
  { label: t("UPGLV.filterTypeSvc001"), value: 1 },
  { label: t("UPGLV.filterTypeSvc004"), value: 2 },
  { label: t("UPGLV.filterTypeSvc003"), value: 3 },
  { label: t("UPGLV.filterTypeSvc005"), value: 4 },
]);

const resultStatusOptions = computed(() => [
  { value: 0, label: t("UPGLV.filterResultStatusPending") },
  { value: 1, label: t("UPGLV.filterResultStatusWaiting") },
  { value: 2, label: t("UPGLV.filterResultStatusRunning") },
  { value: 3, label: t("UPGLV.filterResultStatusSuccess") },
  { value: 4, label: t("UPGLV.filterResultStatusFail") },
  { value: 5, label: t("UPGLV.filterResultStatusCanceled") },
  { value: 6, label: t("UPGLV.filterResultStatusExpired") },
  { value: 7, label: t("UPGLV.filterResultStatusPartial") },
]);

const attitudeOptions = computed(() => [
  { value: 0, label: t("UPGLV.filterAttitudePending") },
  { value: 1, label: t("UPGLV.filterAttitudeAgree") },
  { value: 2, label: t("UPGLV.filterAttitudeReject") },
]);

const stats = computed(() => {
  const list = recordList.value;
  const get = (s: number) => list.filter((r) => r.resultStatus === s).length;
  const completed = get(3) + get(7); // 成功 + 部分成功
  const processing = get(0) + get(1) + get(2); // 待确认 + 等待执行 + 升级中
  const failed = get(4) + get(5); // 升级失败 + 已取消
  const expired = get(6);
  const total = list.length;
  const successRate = total ? Math.round((completed / total) * 100) : 0;
  return {
    total,
    successRate,
    completed,
    successCount: get(3),
    partialCount: get(7),
    processing,
    pending: get(0) + get(1),
    upgrading: get(2),
    failed,
    rejected: 0, // 当前 schema 暂未拆分 responseStatus,后续可细化
    upgradeFailed: get(4),
    expired,
  };
});

const buildParams = () => {
  const params: Record<string, unknown> = {
    current: currentPage.value,
    size: pageSize,
  };
  if (query.type) params.type = query.type;
  return params;
};

const filterClientSide = (records: PushRecord[]) => {
  return records.filter((r) => {
    if (query.resultStatus !== null && r.resultStatus !== query.resultStatus)
      return false;
    if (
      query.customerAttitude !== null &&
      r.customerAttitude !== query.customerAttitude
    )
      return false;
    return true;
  });
};

const loadData = async (showToast = false) => {
  loading.value = true;
  try {
    const response = await getPushRecordListApi(buildParams());
    if (response) {
      let records = response.records || [];
      records = filterClientSide(records);
      recordList.value = records;
      total.value = response.total || 0;
      lastRefreshTime.value = new Date().toLocaleTimeString("zh-CN", {
        hour12: false,
      });
      const expandedIds = Object.keys(expandedItems.value);
      expandedItems.value = {};
      for (const id of expandedIds) {
        const r = await getPushRecordItemsApi(id);
        if (r) expandedItems.value[id] = r;
      }
      if (showToast) ElMessage.success("已刷新");
    }
  } finally {
    loading.value = false;
  }
};

const handleQuery = () => {
  currentPage.value = 1;
  loadData();
};

// ==================== 终端固件名（Svc001 [1,4]/[1,5]） ====================
// 注：终端固件名不在推送记录列表中展示（语义错位——固件名是终端属性，非推送事件属性），
// 仅在「创建升级计划」页（CreateUpgradePlanView）中通过 queryEndpointFirmwareApi 展示。

/**
 * 刷新按钮：刷新推送记录列表
 */
const handleRefresh = async () => {
  await loadData(true);
  ElMessage.success("已刷新");
};
const handleReset = () => {
  query.type = null;
  query.resultStatus = null;
  query.customerAttitude = null;
  query.timeRange = [];
  currentPage.value = 1;
  loadData();
};
const handlePageChange = (current: number) => {
  currentPage.value = current;
  loadData();
};

const handleExpandChange = async (row: PushRecord) => {
  if (row.type !== 2 && row.type !== 4) return;
  const r = await getPushRecordItemsApi(row.id);
  if (r) expandedItems.value[row.id] = r;
};

const openDetail = (row: PushRecord) => {
  currentDetail.value = row;
  detailItems.value = [];
  detailVisible.value = true;
};

const onDrawerOpen = async () => {
  if (!currentDetail.value) return;
  if (currentDetail.value.type !== 2 && currentDetail.value.type !== 4) return;
  detailItemsLoading.value = true;
  try {
    const r = await getPushRecordItemsApi(currentDetail.value.id);
    detailItems.value = r || [];
  } finally {
    detailItemsLoading.value = false;
  }
};

const canCancel = (row: PushRecord) =>
  row.resultStatus >= 0 && row.resultStatus <= 2;

// 重试功能暂不开放，返回 false 隐藏所有重试按钮
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const canRetry = (_row: PushRecord) => false;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleRetry = async (_row: PushRecord) => {
  ElMessage.info("重试功能待后端接口提供,当前记录已重新加入待处理队列");
  // TODO: 调用后端重试接口（需要 manager-server 提供 POST /pushRecord/retry/{id}）
  // const ok = await retryPushRecordApi(row.id);
  // if (ok) { ElMessage.success("已重试"); loadData(); }
};

// ============ 数字滚动动画 ============
type AnimatableStats = ReturnType<typeof stats.value>;
const displayStats = ref<AnimatableStats>({
  total: 0,
  successRate: 0,
  completed: 0,
  successCount: 0,
  partialCount: 0,
  processing: 0,
  pending: 0,
  upgrading: 0,
  failed: 0,
  rejected: 0,
  upgradeFailed: 0,
  expired: 0,
});

const animateNumber = (
  getter: () => number,
  setter: (v: number) => void,
  target: number,
  duration = 700
) => {
  const start = getter();
  if (start === target) return;
  const startTime = performance.now();
  const step = (now: number) => {
    const progress = Math.min((now - startTime) / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
    setter(Math.round(start + (target - start) * eased));
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
};

watch(
  () => stats.value,
  (nv) => {
    const cur = displayStats.value;
    animateNumber(
      () => cur.total,
      (v) => (cur.total = v),
      nv.total
    );
    animateNumber(
      () => cur.successRate,
      (v) => (cur.successRate = v),
      nv.successRate
    );
    animateNumber(
      () => cur.completed,
      (v) => (cur.completed = v),
      nv.completed
    );
    animateNumber(
      () => cur.successCount,
      (v) => (cur.successCount = v),
      nv.successCount
    );
    animateNumber(
      () => cur.partialCount,
      (v) => (cur.partialCount = v),
      nv.partialCount
    );
    animateNumber(
      () => cur.processing,
      (v) => (cur.processing = v),
      nv.processing
    );
    animateNumber(
      () => cur.pending,
      (v) => (cur.pending = v),
      nv.pending
    );
    animateNumber(
      () => cur.upgrading,
      (v) => (cur.upgrading = v),
      nv.upgrading
    );
    animateNumber(
      () => cur.failed,
      (v) => (cur.failed = v),
      nv.failed
    );
    animateNumber(
      () => cur.upgradeFailed,
      (v) => (cur.upgradeFailed = v),
      nv.upgradeFailed
    );
    animateNumber(
      () => cur.expired,
      (v) => (cur.expired = v),
      nv.expired
    );
  },
  { deep: true, immediate: true }
);

const getRowClassName = ({ row }: { row: PushRecord }) => {
  const classes: string[] = [];
  if (row.type !== 2 && row.type !== 4) {
    classes.push("row-no-expand");
  }
  if (row.resultStatus != null) {
    classes.push(`row-status-${row.resultStatus}`);
  }
  return classes.join(" ");
};

// 判断字符串是否像 URL（历史误用 detail 字段存 URL 的兼容判断）
const isUrlLike = (s: string | null | undefined): boolean => {
  if (!s) return false;
  return /^(https?|mttps|ftp):\/\//i.test(s.trim());
};

// 解析推送记录的文件信息（展示文件名/MD5 及 Svc001 三文件的 MD5/签名文件名，不展示下载 URL）
const fileInfo = computed(() => {
  const record = currentDetail.value;
  if (!record) return null;
  // 1. 主表自身的文件字段（svc001 终端升级 / svc003 终端协议 / svc004 设备协议）
  if (record.fileName || record.fileMd5 || record.filePath) {
    // Svc001 三文件：MD5/签名文件名存于 extraJson（{"md5FileName":"x.md5","signFileName":"x.sig"}）
    let md5FileName = "";
    let signFileName = "";
    if (record.extraJson) {
      try {
        const extra =
          typeof record.extraJson === "string"
            ? JSON.parse(record.extraJson)
            : record.extraJson;
        md5FileName = extra.md5FileName || "";
        signFileName = extra.signFileName || "";
      } catch {
        // extraJson 非法时忽略（历史数据兼容）
      }
    }
    return {
      fileName: record.fileName || "-",
      fileMd5: record.fileMd5 || "",
      md5FileName,
      signFileName,
    };
  }
  return null;
});

const handleCancel = async (row: PushRecord) => {
  try {
    await ElMessageBox.confirm(
      t("UPGLV.confirmCancelText"),
      t("UPGLV.confirmCancelTitle"),
      { type: "warning", confirmButtonText: "确定", cancelButtonText: "取消" }
    );
  } catch {
    return;
  }
  loadingMap.value.set("cancel-" + row.id, true);
  try {
    const response = await cancelPushRecordApi(row.id);
    if (response) {
      ElMessage.success("取消成功");
      await loadData();
    } else {
      ElMessage.error("取消失败");
    }
  } finally {
    loadingMap.value.set("cancel-" + row.id, false);
  }
};

watch(autoRefresh, (val) => {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
  if (val)
    refreshTimer = window.setInterval(() => loadData(), REFRESH_INTERVAL);
});

onBeforeUnmount(() => {
  if (refreshTimer) clearInterval(refreshTimer);
});

const shortId = (id: string | null | undefined, len = 8) => {
  if (!id) return "-";
  return id.length > len ? id.substring(0, len) + "..." : id;
};

const progressPct = (row: PushRecord) => {
  if (!row.totalItems) return 0;
  return Math.round((row.successItems / row.totalItems) * 100);
};

const progressStatus = (resultStatus: number) => {
  if (resultStatus === 3) return "success";
  if (resultStatus === 4 || resultStatus === 6) return "exception";
  if (resultStatus === 7) return "warning";
  return undefined;
};

const formatTime = (time: string | null | undefined) => {
  if (!time) return "-";
  try {
    return new Date(time).toLocaleString("zh-CN", { hour12: false });
  } catch {
    return time;
  }
};

const nameOfPush = (s: number) =>
  ({ 0: "未推送", 1: "已推送", 2: "推送失败" }[s] ?? "-");
const tagOfPush = (s: number) =>
  ({ 0: "info", 1: "success", 2: "danger" }[s] ?? "info");
const nameOfResp = (s: number) =>
  ({ 0: "等待响应", 1: "已接受", 2: "已拒绝" }[s] ?? "-");
const tagOfResp = (s: number) =>
  ({ 0: "info", 1: "success", 2: "danger" }[s] ?? "info");
const nameOfResult = (s: number) =>
  ({
    0: "待确认",
    1: "等待执行",
    2: "升级中",
    3: "成功",
    4: "失败",
    5: "已取消",
    6: "已过期",
    7: "部分成功",
  }[s] ?? "未知");

// 按业务类型细分文案：协议类(type=3/4)用"已下发"代替"升级中"，超时显示"已过期未回应"
const nameOfResultByType = (s: number, type: number | null | undefined) => {
  if ((type === 3 || type === 4) && s === 2) return "已下发";
  if ((type === 3 || type === 4) && s === 6) return "已过期未回应";
  return nameOfResult(s);
};
const tagOfResult = (s: number) =>
  ({
    0: "info",
    1: "primary",
    2: "warning",
    3: "success",
    4: "danger",
    5: "info",
    6: "warning",
    7: "warning",
  }[s] ?? "info");
const timelineColorOfResult = (s: number) =>
  ({ 3: "#67c23a", 4: "#f56c6c", 6: "#e6a23c", 7: "#e6a23c" }[s] ?? "#909399");
const timelineColorOfResp = (s: number) =>
  ({ 1: "#67c23a", 2: "#f56c6c" }[s] ?? "#909399");
const nameOfAttitude = (s: number | null | undefined) =>
  ({ 0: "待确认", 1: "已同意", 2: "已拒绝" }[s ?? -1] ?? "-");
const tagOfAttitude = (s: number | null | undefined) =>
  ({ 0: "info", 1: "success", 2: "danger" }[s ?? -1] ?? "info");
const nameOfItemResult = (s: number) =>
  ({ 1: "待执行", 2: "升级中", 3: "成功", 4: "失败", 5: "已跳过", 6: "已取消" }[
    s
  ] ?? "-");
const tagOfItemResult = (s: number) =>
  ({ 1: "info", 2: "warning", 3: "success", 4: "danger", 5: "info", 6: "info" }[
    s
  ] ?? "info");
const getSubTypeName = (subType: number) =>
  ({ 1: "BCU", 2: "BSMU", 3: "BAMU", 4: "ITMU", 5: "ITPU", 6: "EMU" }[
    subType
  ] ?? "-");

onMounted(async () => {
  // 确保翻译表就绪：登录后首次进入时 App 挂载阶段的加载因未登录(401)失败，
  // devName 列翻译依赖此缓存；与 App 启动加载共享同一次请求
  await loadTranslations();
  await loadData();
});
</script>

<style lang="less" scoped>
.push-record-page {
  padding: 20px;
  width: 100%;
  background: radial-gradient(
      at 20% 0%,
      rgba(64, 158, 255, 0.08) 0px,
      transparent 50%
    ),
    radial-gradient(at 80% 100%, rgba(6, 182, 212, 0.06) 0px, transparent 50%),
    linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
  background-attachment: fixed;
  min-height: 100vh;
  box-sizing: border-box;
}

.page-header {
  position: relative;
  background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 45%, #0891b2 100%);
  color: #fff;
  padding: 30px 36px;
  border-radius: 16px;
  margin-bottom: 22px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(37, 99, 235, 0.22);
  &::before {
    content: "";
    position: absolute;
    top: -60%;
    right: -8%;
    width: 480px;
    height: 480px;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.18) 0%,
      transparent 70%
    );
    border-radius: 50%;
    pointer-events: none;
  }
  &::after {
    content: "";
    position: absolute;
    bottom: -40%;
    left: 10%;
    width: 280px;
    height: 280px;
    background: radial-gradient(
      circle,
      rgba(6, 182, 212, 0.4) 0%,
      transparent 70%
    );
    border-radius: 50%;
    pointer-events: none;
  }
  .header-bg {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    .header-svg {
      width: 100%;
      height: 100%;
    }
  }
  .title-block {
    position: relative;
    z-index: 1;
    .page-title {
      font-size: 28px;
      font-weight: 700;
      color: #fff;
      margin: 0 0 6px;
      letter-spacing: 0.5px;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
    .page-desc {
      color: rgba(255, 255, 255, 0.85);
      font-size: 13px;
      margin: 0;
      letter-spacing: 0.3px;
    }
  }
  .header-actions {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 16px;
    .last-refresh {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.85);
      padding: 4px 10px;
      background: rgba(255, 255, 255, 0.12);
      border-radius: 12px;
      backdrop-filter: blur(8px);
    }
    .auto-refresh-switch {
      :deep(.el-switch__label) {
        color: rgba(255, 255, 255, 0.85);
      }
    }
    .last-refresh {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      .live-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.5);
        transition: background 0.3s ease;
      }
      .live-dot.active {
        background: #34d399;
        box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.6);
        animation: livePulse 1.6s ease-out infinite;
      }
    }
    .refresh-btn {
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 18px;
      border: none;
      border-radius: 10px;
      background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%);
      color: #1e3a8a;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.3px;
      cursor: pointer;
      overflow: hidden;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12),
        inset 0 1px 0 rgba(255, 255, 255, 0.6);
      transition: transform 0.2s ease, box-shadow 0.3s ease;
      .ripple {
        position: absolute;
        inset: 0;
        background: radial-gradient(
          circle at center,
          rgba(37, 99, 235, 0.3) 0%,
          transparent 70%
        );
        opacity: 0;
        transform: scale(0.5);
        transition: opacity 0.4s ease, transform 0.6s ease;
        pointer-events: none;
      }
      .refresh-icon {
        font-size: 16px;
        transition: transform 0.5s ease;
      }
      &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18),
          inset 0 1px 0 rgba(255, 255, 255, 0.6);
        .ripple {
          opacity: 1;
          transform: scale(1.5);
        }
      }
      &:active:not(:disabled) {
        transform: translateY(0);
      }
      &.loading .refresh-icon {
        animation: spin 1s linear infinite;
      }
      &:disabled {
        cursor: not-allowed;
        opacity: 0.7;
      }
    }
  }
}

@keyframes livePulse {
  0% {
    box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.7);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(52, 211, 153, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(52, 211, 153, 0);
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.stat-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
  margin-bottom: 18px;
}

.stat-card {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: #fff;
  border-radius: 14px;
  padding: 20px 22px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
  overflow: hidden;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease;
  cursor: default;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: currentColor;
    transition: width 0.3s ease;
  }
  &::after {
    content: "";
    position: absolute;
    top: -50px;
    right: -50px;
    width: 140px;
    height: 140px;
    background: radial-gradient(circle, currentColor 0%, transparent 70%);
    opacity: 0.06;
    border-radius: 50%;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04);
    &::after {
      opacity: 0.14;
    }
    &::before {
      width: 6px;
    }
    .stat-icon {
      transform: scale(1.1) rotate(-5deg);
    }
  }
  .stat-icon-wrap {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: linear-gradient(135deg, currentColor 0%, currentColor 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    position: relative;
    overflow: hidden;
    &::after {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.25) 0%,
        transparent 100%
      );
    }
    .stat-icon {
      font-size: 22px;
      color: #fff !important;
      transition: transform 0.3s ease;
      position: relative;
      z-index: 1;
    }
    .stat-icon.is-loading {
      animation: rotate 1.4s linear infinite;
    }
  }
  .stat-body {
    flex: 1;
    min-width: 0;
  }
  .stat-label {
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.3px;
  }
  .stat-value {
    font-size: 30px;
    font-weight: 700;
    line-height: 1.1;
    margin: 6px 0 4px;
    color: currentColor;
    letter-spacing: -0.5px;
    font-variant-numeric: tabular-nums;
  }
  .stat-progress {
    height: 4px;
    background: rgba(0, 0, 0, 0.06);
    border-radius: 2px;
    overflow: hidden;
    margin: 4px 0 6px;
  }
  .stat-progress-bar {
    height: 100%;
    background: currentColor;
    border-radius: 2px;
    transition: width 0.7s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 0 8px currentColor;
  }
  .stat-tip {
    font-size: 11px;
    color: #9ca3af;
    letter-spacing: 0.2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .stat-rate {
    color: currentColor;
    font-size: 13px;
    margin: 0 2px;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.stat-total {
  border-left-color: #409eff;
  .stat-value {
    color: #409eff;
  }
}
.stat-completed {
  border-left-color: #67c23a;
  .stat-value {
    color: #67c23a;
  }
}
.stat-processing {
  border-left-color: #e6a23c;
  .stat-value {
    color: #e6a23c;
  }
}
.stat-failed {
  border-left-color: #f56c6c;
  .stat-value {
    color: #f56c6c;
  }
}
.stat-expired {
  border-left-color: #909399;
  .stat-value {
    color: #909399;
  }
}

.filter-bar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 12px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.04);
  .filter-select {
    width: 160px;
  }
  .filter-date {
    width: 360px;
  }
  .action-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 18px;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.3px;
    cursor: pointer;
    transition: all 0.25s ease;
    overflow: hidden;
    .btn-icon {
      font-size: 14px;
    }
    .btn-icon-spin {
      transition: transform 0.5s ease;
    }
    &:hover .btn-icon-spin {
      transform: rotate(-180deg);
    }
  }
  .btn-primary {
    background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
    color: #fff;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 18px rgba(37, 99, 235, 0.4),
        inset 0 1px 0 rgba(255, 255, 255, 0.3);
    }
  }
  .btn-ghost {
    background: #f1f5f9;
    color: #475569;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
    &:hover {
      background: #e2e8f0;
      color: #1e293b;
    }
  }
}

// ============ 表格行操作按钮组 ============
.row-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
}
.row-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  .el-icon.is-loading {
    animation: spin 1s linear infinite;
  }
}
.btn-view {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.08);
  &:hover:not(:disabled) {
    background: rgba(59, 130, 246, 0.15);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);
  }
}
.btn-retry {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.08);
  &:hover:not(:disabled) {
    background: rgba(245, 158, 11, 0.15);
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.25);
  }
}
.btn-danger {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.08);
  &:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.15);
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
  }
}

// ============ 抽屉标题区操作按钮 ============
.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-right: 12px;
  .drawer-title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
  }
  .drawer-actions {
    display: flex;
    gap: 8px;
  }
  .drawer-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px 12px;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
    .el-icon.is-loading {
      animation: spin 1s linear infinite;
    }
  }
  .btn-retry {
    background: rgba(245, 158, 11, 0.1);
    color: #d97706;
    &:hover:not(:disabled) {
      background: rgba(245, 158, 11, 0.2);
    }
  }
  .btn-danger {
    background: rgba(239, 68, 68, 0.1);
    color: #dc2626;
    &:hover:not(:disabled) {
      background: rgba(239, 68, 68, 0.2);
    }
  }
}

// 无子项的行不显示展开箭头
:deep(.row-no-expand .el-table__expand-icon) {
  visibility: hidden;
  cursor: default;
}

.table-card {
  background: #fff;
  border-radius: 10px;
  padding: 8px 16px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }
}

.record-table {
  table-layout: fixed;
  width: 100%;
  :deep(.el-table__body-wrapper) {
    overflow-x: hidden;
  }
  :deep(th.el-table__cell) {
    background: #f8fafc !important;
    color: #475569;
    font-weight: 600;
    font-size: 13px;
    border-bottom: 1px solid #e5e7eb;
  }
  :deep(th.el-table__cell),
  :deep(td.el-table__cell) {
    white-space: nowrap;
  }
  :deep(.el-table__body tr) {
    transition: all 0.25s ease;
    position: relative;
  }
  // 行左侧色条（按状态着色）
  :deep(.el-table__body tr td.el-table__cell:first-child::before) {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    transition: width 0.2s ease;
  }
  :deep(.el-table__body tr:hover > td.el-table__cell) {
    background: #f0f7ff !important;
  }
  :deep(.el-table__body tr:hover td.el-table__cell:first-child::before) {
    width: 5px;
  }
  // 行状态左侧色条
  :deep(tr.row-status-3 td.el-table__cell:first-child::before) {
    background: #67c23a;
  }
  :deep(tr.row-status-7 td.el-table__cell:first-child::before) {
    background: #e6a23c;
  }
  :deep(tr.row-status-4 td.el-table__cell:first-child::before),
  :deep(tr.row-status-5 td.el-table__cell:first-child::before) {
    background: #f56c6c;
  }
  :deep(tr.row-status-6 td.el-table__cell:first-child::before) {
    background: #909399;
  }
  :deep(tr.row-status-2 td.el-table__cell:first-child::before) {
    background: #e6a23c;
    animation: pulseBar 1.6s ease-in-out infinite;
  }
  // 进行中行背景脉冲
  :deep(tr.row-status-2) {
    animation: pulseBg 1.6s ease-in-out infinite;
  }
  :deep(.el-table__body tr td.el-table__cell) {
    border-bottom: 1px solid #f1f5f9;
  }
  :deep(.el-table__expanded-cell) {
    background: #fafbfc !important;
    padding: 14px 20px !important;
    white-space: nowrap;
  }
}

@keyframes pulseBg {
  0%,
  100% {
    background-color: rgba(230, 162, 60, 0.02);
  }
  50% {
    background-color: rgba(230, 162, 60, 0.08);
  }
}

@keyframes pulseBar {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

// 自定义滚动条
:deep(::-webkit-scrollbar) {
  width: 8px;
  height: 8px;
}
:deep(::-webkit-scrollbar-track) {
  background: transparent;
}
:deep(::-webkit-scrollbar-thumb) {
  background: #cbd5e1;
  border-radius: 4px;
  &:hover {
    background: #94a3b8;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 0;
  color: #9ca3af;
  .empty-icon {
    font-size: 48px;
    margin-bottom: 8px;
  }
  .empty-text {
    font-size: 14px;
    color: #6b7280;
  }
  .empty-hint {
    font-size: 12px;
    color: #9ca3af;
    margin-top: 4px;
  }
}

.type-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  .type-icon {
    font-size: 16px;
  }
}

.plan-cell {
  display: flex;
  flex-direction: column;
  .plan-name {
    color: #1f2937;
    font-weight: 500;
  }
  .plan-ref {
    color: #9ca3af;
    font-size: 11px;
    font-family: monospace;
    margin-top: 2px;
  }
}

.ship-cell {
  display: flex;
  flex-direction: column;
  .ship-name {
    color: #1f2937;
  }
  .ship-id {
    color: #9ca3af;
    font-size: 11px;
    font-family: monospace;
  }
}

.progress-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  :deep(.el-progress) {
    width: 110px;
  }
  .progress-text {
    font-size: 12px;
    color: #6b7280;
  }
}

.time-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
  .time-row {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .time-tag {
    display: inline-block;
    padding: 0 6px;
    background: #eef2f7;
    color: #4b5563;
    border-radius: 3px;
    font-size: 11px;
    line-height: 16px;
    flex-shrink: 0;
  }
  .time-sub {
    color: #9ca3af;
  }
}

.muted {
  color: #9ca3af;
}

.expand-area {
  padding: 4px 0;
}
.expand-loading {
  display: flex;
  justify-content: center;
  padding: 20px;
  color: #409eff;
}

// ============ 详情抽屉 ============
.detail-content {
  padding: 0 8px 24px;
}

.detail-header {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 4px 0 20px;
  border-bottom: 1px solid #f0f1f2;
  margin-bottom: 18px;
  .detail-plan-name {
    width: 100%;
    margin-top: 8px;
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
  }
}

.detail-section {
  margin-bottom: 24px;
  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 12px;
    padding-left: 8px;
    border-left: 3px solid #409eff;
    .section-count {
      font-weight: 400;
      color: #9ca3af;
      margin-left: 4px;
    }
  }
}

.timeline-label {
  font-size: 13px;
  color: #1f2937;
  margin-right: 8px;
}

.mono {
  font-family: monospace;
  color: #4b5563;
}

// ============ 文件信息卡片 ============
.file-card {
  display: flex;
  gap: 14px;
  padding: 16px 18px;
  background: linear-gradient(
    135deg,
    rgba(64, 158, 255, 0.05) 0%,
    rgba(6, 182, 212, 0.04) 100%
  );
  border: 1px solid rgba(64, 158, 255, 0.18);
  border-radius: 10px;
  .file-icon {
    flex-shrink: 0;
    font-size: 32px;
    color: #409eff;
    margin-top: 2px;
  }
  .file-info {
    flex: 1;
    min-width: 0;
  }
  .file-name {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 8px;
    word-break: break-all;
  }
  .file-meta {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 4px;
    font-size: 12px;
    line-height: 1.6;
    .meta-label {
      flex-shrink: 0;
      padding: 1px 8px;
      background: rgba(64, 158, 255, 0.1);
      color: #2563eb;
      border-radius: 3px;
      font-weight: 500;
      font-size: 11px;
      line-height: 18px;
    }
    .meta-value {
      flex: 1;
      min-width: 0;
      word-break: break-all;
    }
  }
}
</style>
